import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
  Body,
  Container,
  Head,
  Heading,
  Html,
  Img,
  Preview,
  Section,
  Text,
} from '@react-email/components';

export interface TruckAnniversaryEmailProps {
  customerName: string;
  /** Plain-text personalized message written by the sales rep. Newlines are preserved. */
  personalMessage?: string;
  /** Optional sender name shown in the sign-off. */
  senderName?: string;
}

export const truckAnniversaryEmail = async (
  params: TruckAnniversaryEmailProps,
): Promise<{ subject: string; html: string }> => {
  const { customerName, personalMessage, senderName } = params;
  const greetingName = (customerName || '').trim().split(/\s+/)[0] || 'friend';
  const subject = `Celebrating your truck anniversary, ${greetingName}!`;

  const emailComponent = (
    <Html>
      <Head />
      <Preview>Marking the anniversary of your truck purchase with Optic Truck Works.</Preview>
      <Body
        style={{
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#f5f5f5',
          margin: '0',
          padding: '0',
        }}
      >
        <Container
          style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginTop: '40px',
            marginBottom: '40px',
          }}
        >
          <Section
            style={{
              padding: '32px',
              textAlign: 'center' as const,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Img
              src="https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FLogoVariation-OpticTruckWorks-05.png?alt=media&token=c3428c05-276c-4d32-ac33-a7d9d51fe380"
              alt="Optic Truck Works Logo"
              style={{ maxWidth: '200px', height: 'auto', margin: '0 auto' }}
            />
          </Section>

          <Section style={{ padding: '32px' }}>
            <Heading
              style={{
                fontSize: '26px',
                fontWeight: '600',
                color: '#2e7d32',
                marginBottom: '24px',
                textAlign: 'center' as const,
              }}
            >
              Cheers to another year on the road, {greetingName}!
            </Heading>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '16px',
              }}
            >
              Hi {customerName || 'there'},
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              It’s been another year since we put you behind the wheel of one of our trucks, and we wanted to
              take a moment to thank you for trusting Optic Truck Works to keep your business moving. We hope
              the truck is treating you well and that your operation is rolling stronger than ever.
            </Text>

            {personalMessage && personalMessage.trim().length > 0 && (
              <Text
                style={{
                  fontSize: '16px',
                  color: '#333333',
                  lineHeight: '1.6',
                  marginBottom: '24px',
                  whiteSpace: 'pre-line' as const,
                }}
              >
                {personalMessage}
              </Text>
            )}

            <Text
              style={{
                fontSize: '16px',
                color: '#333333',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              If you’re thinking about adding to your fleet, upgrading your current rig, or just want to talk
              about what’s coming up next, we’d love to chat. We’re always here to help with the next truck
              when the time is right.
            </Text>

            <Text
              style={{
                fontSize: '16px',
                color: '#666666',
                lineHeight: '1.6',
                borderTop: '1px solid #e0e0e0',
                paddingTop: '24px',
                marginTop: '32px',
              }}
            >
              Thanks again for the trust,
              <br />
              {senderName ? `${senderName} — ` : ''}The Optic Truck Works Team
            </Text>
          </Section>
        </Container>
      </Body>
    </Html>
  );

  const html = `<!DOCTYPE html>${renderToStaticMarkup(emailComponent)}`;
  return { subject, html };
};
